import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import './TransactionDetails.css';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import { useNavigate, useParams } from 'react-router-dom';
import PaymentMethodIcon from '../../../../../assets/Admin/Payments/payment-method-icon.svg';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { formatDateFull } from '../../../../../utils/dateUtils';
import TransactionDetailsSkeleton from '../../../../../components/SkeletonEffect/TransactionDetailsSkeleton/TransactionDetailsSkeleton';

const UserRoleColor = {
  Learner: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
  Instructor: { color: '#BC8D00', bgcolor: '#FFF3CE', value: 'Instructor' },
};

const StatusColor = {
  Completed: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Completed' },
  Failed: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Failed' },
  Pending: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Pending' },
};

const TransactionDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [transactionDetails, setTransactionDetails] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/admin/transactions/${id}`);
        const rec = response.data;

        // Helper mappers
        const mapStatus = raw => {
          if (!raw) return raw;
          const lower = raw.toLowerCase();
          if (lower === 'success' || lower === 'completed') return 'Completed';
          if (lower === 'failed') return 'Failed';
          if (lower === 'pending') return 'Pending';
          return raw;
        };

        const mapped = {
          paymentId: rec.id,
          name: `${rec.first_name ?? ''} ${rec.last_name ?? ''}`.trim(),
          userRole:
            rec.user_role?.toLowerCase() === 'student' || rec.user_role?.toLowerCase() === 'learner'
              ? 'Learner'
              : rec.user_role?.toLowerCase() === 'teacher' ||
                  rec.user_role?.toLowerCase() === 'instructor'
                ? 'Instructor'
                : rec.user_role,
          paymentFor:
            rec.payment_type?.toLowerCase() === 'wallet top up'
              ? 'Wallet Top Up'
              : 'Application Fee',
          dateTime: rec.date_time,
          amount: rec.amount,
          koins: rec.koins,
          status: mapStatus(rec.status),
          country: rec.country,
          city: rec.city,
          currency: 'Riyal',
          paymentMode: rec.payment_mode,
          apsPaymentFortId: rec.aps_payment_fort_id,
          apsPaymentMerchantReference: rec.aps_payment_merchant_reference,
          createdAt: rec.created_at,
          updatedAt: rec.updated_at,
        };

        setTransactionDetails(mapped);
      } catch (err) {
        console.error('Error fetching transaction details:', err);
        // If error is 404, set transactionDetails to null to show "Transaction not found"
        if (err.response?.status === 404) {
          setTransactionDetails(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  if (loading) {
    return (
      <Layout title="Klass Online | Transaction Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <TransactionDetailsSkeleton />
          </Box>
        </Box>
      </Layout>
    );
  }

  if (!transactionDetails) {
    return (
      <Layout title="Klass Online | Transaction Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Box className="flex flex-col p-[1rem] gap-[10px]">
              <Typography>Transaction not found</Typography>
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  const formattedDate = formatDateFull(transactionDetails.dateTime);

  return (
    <Layout title="Klass Online | Transaction Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-[10px]">
            <Box className="flex flex-col gap-[5px]">
              <Box className="flex items-center gap-1">
                <Typography
                  className="pointer-text"
                  onClick={() => navigate('/admin/payments/transactions')}
                >
                  Payments {'>'}
                  {'>'} Transaction {'>'}
                  {'>'}
                </Typography>
                <Typography className="view-text">View</Typography>
              </Box>
              <div className="dispute-header">
                <Typography className="dispute-header-title">
                  Payment ID #{transactionDetails.paymentId}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    borderRadius: '20px',
                    border: `1px solid QR{StatusColor[transactionDetails.status].color}`,
                    backgroundColor: StatusColor[transactionDetails.status].bgcolor,
                    color: StatusColor[transactionDetails.status].color,
                    padding: '2px 8px',
                    maxWidth: 'fit-content',
                  }}
                >
                  <Box
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: StatusColor[transactionDetails.status].color,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {StatusColor[transactionDetails.status].value}
                  </Typography>
                </Box>
              </div>
              <Typography variant="body2" color="textSecondary">
                {formattedDate}
              </Typography>
            </Box>
            <div className="details-section">
              <h3 className="details-title">Personal Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name</span>
                  <span className="info-value">{transactionDetails.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Payment For</span>
                  <span className="info-value">{transactionDetails.paymentFor}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">User Role</span>
                  <div
                    className="role-tag"
                    style={{
                      backgroundColor: UserRoleColor[transactionDetails.userRole].bgcolor,
                      color: UserRoleColor[transactionDetails.userRole].color,
                    }}
                  >
                    {UserRoleColor[transactionDetails.userRole].value}
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-label">Current Location</span>
                  <span className="info-value">
                    {(() => {
                      const city = transactionDetails.city;
                      const country = transactionDetails.country;
                      if (city && country && city !== country) {
                        return `${city}, ${country}`;
                      }
                      if (city) {
                        return city;
                      }
                      if (country) {
                        return country;
                      }
                      return '---';
                    })()}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Currency</span>
                  <span className="info-value">{transactionDetails.currency}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Payment Mode</span>
                  <span className="info-value">{transactionDetails.paymentMode}</span>
                </div>
                {transactionDetails.koins && (
                  <div className="info-item">
                    <span className="info-label">Koins Earned</span>
                    <span className="info-value">{transactionDetails.koins}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="details-section">
              <h3 className="details-title">Payment Details</h3>

              <div className="refund-item">
                <div className="refund-details">
                  <span className="detail-subtitle">{transactionDetails.paymentFor}</span>
                  <span className="refund-time">{formattedDate}</span>
                </div>

                <span
                  style={{
                    color: 'black',
                    fontSize: 16,
                    fontFamily: 'Poppins',
                    fontWeight: '400',
                    wordWrap: 'break-word',
                  }}
                >
                  {transactionDetails.amount} Riyal
                </span>
              </div>

              <div className="amount-breakdown">
                <div className="amount-row">
                  <span>Subtotal</span>
                  <span>{transactionDetails.amount} Riyal</span>
                </div>
                <div className="amount-row total-row">
                  <span>Total</span>
                  <span>{transactionDetails.amount} Riyal</span>
                </div>
              </div>
            </div>

            {(transactionDetails.apsPaymentFortId ||
              transactionDetails.apsPaymentMerchantReference) && (
              <div className="details-section">
                <h3 className="details-title">APS Payment Details</h3>
                <div className="info-grid">
                  {transactionDetails.apsPaymentFortId && (
                    <div className="info-item">
                      <span className="info-label">APS Payment Fort ID</span>
                      <span className="info-value">{transactionDetails.apsPaymentFortId}</span>
                    </div>
                  )}
                  {transactionDetails.apsPaymentMerchantReference && (
                    <div className="info-item">
                      <span className="info-label">APS Merchant Reference</span>
                      <span className="info-value">
                        {transactionDetails.apsPaymentMerchantReference}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {transactionDetails.userRole === 'Learner' && (
              <div className="details-section">
                <h3 className="details-title">Payment Mode</h3>

                <div
                  style={{
                    width: '100%',
                    paddingLeft: 20,
                    wordWrap: 'break-word',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {transactionDetails.paymentMode === 'VISA' && (
                    <img
                      src={PaymentMethodIcon}
                      alt="VISA"
                      style={{ width: '40px', height: 'auto' }}
                    />
                  )}
                  <span className="info-value">{transactionDetails.paymentMode}</span>
                </div>
              </div>
            )}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default TransactionDetails;
